import { useQuery } from "@tanstack/react-query";
import {
  ProductAvailabilityClient,
  type ProductAvailabilityResponse,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { EcommerceQueryKeys } from "./ecommerceQueryKeys";

export interface ProductAvailabilityResult {
  data: ProductAvailabilityResponse | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Fetches AX-warehouse stock availability for a product, keyed by org unit + descriptor.
 * Authorization is enforced server-side; callers hide the Stock / Inventory tab when the
 * response has no rows. Unlike useStandardsProductAvailability this applies no client-side
 * permission gate so read-only surfaces (orders, quotes) can surface stock when available.
 */
export default function useProductAvailability(
  productDescriptor: string | null | undefined,
  orgUnitId: number | null | undefined,
  enabled: boolean,
): ProductAvailabilityResult {
  const api = useApi(ProductAvailabilityClient);

  const query = useQuery({
    queryKey: [
      EcommerceQueryKeys.ProductAvailability,
      { productDescriptor, orgUnitId },
    ],
    queryFn: async (): Promise<ProductAvailabilityResponse | null> => {
      if (!productDescriptor || orgUnitId == null) return null;
      return await api.getProductAvailability(orgUnitId, productDescriptor);
    },
    enabled: enabled && orgUnitId != null && !!productDescriptor,
    retry: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isInitialLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
